import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { listCfbSchools } from '../cfbSchoolCatalog';
import type { CfbSchoolCatalogEntry } from '../cfbSchoolCatalogTypes';
import { buildCfbCatalogIndex, type CfbCatalogIndex } from '../cfbBranding/cfbTeamVisualIndex';

type CfbSchoolCatalogContextValue = {
  schools: CfbSchoolCatalogEntry[];
  index: CfbCatalogIndex;
  loading: boolean;
  error: string | null;
};

const CfbSchoolCatalogContext = createContext<CfbSchoolCatalogContextValue | null>(null);

export type CfbSchoolCatalogProviderProps = {
  enabled: boolean;
  children: React.ReactNode;
};

export function CfbSchoolCatalogProvider({ enabled, children }: CfbSchoolCatalogProviderProps) {
  const [schools, setSchools] = useState<CfbSchoolCatalogEntry[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setSchools([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await listCfbSchools();
        if (!cancelled) {
          setSchools(response.schools);
        }
      } catch (err) {
        if (!cancelled) {
          setSchools([]);
          setError(err instanceof Error ? err.message : 'Failed to load CFB school catalog.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const index = useMemo(() => buildCfbCatalogIndex(schools), [schools]);

  const value = useMemo(
    () => ({
      schools,
      index,
      loading,
      error,
    }),
    [schools, index, loading, error]
  );

  return (
    <CfbSchoolCatalogContext.Provider value={value}>{children}</CfbSchoolCatalogContext.Provider>
  );
}

export function useCfbSchoolCatalog(): CfbSchoolCatalogContextValue | null {
  return useContext(CfbSchoolCatalogContext);
}
